<template>
  <SloLoader :loading="loadingAddress" size="xl">
    <article class="personal-details--address-book">
      <div class="pt-lg px-md pb-0 sm:px-lg">
        <h3 class="text-xl fw-strong">
          {{ 'Update Address' | t }}
        </h3>
      </div>
      <form @submit.prevent="handleSubmit" action="#">
        <div class="form-wrapper p-md sm:p-lg">
          <div class="personal-details--address-book--address-form-fields">
            <AddressFormFields
              :countries="countries"
              :regions="regions"
              :original-address="address"
              :errors="addressErrors"
              :add-tool-tip="true"
              :tool-tip-message="'Shipping to US only' | t"
              @addressUpdate="handleAddressUpdate"
            />
          </div>
          <SloField class="self-start">
            <SloLabel class="flex items-center justify-start">
              <SloInput
                v-model="address.defaultAddress"
                :checked="address.defaultAddress"
                :value="true"
                type="checkbox"
                class="mr-sm"
              />
              {{ 'Make Default' | t }}
            </SloLabel>
          </SloField>
        </div>
        <div class="form-footer flex items-center justify-end gap-md">
          <SloButton
            :disabled="loading"
            :to="{name: 'marketplace-account-addresses'}"
            fill="outline"
          >
            {{ 'Cancel' | t }}
          </SloButton>
          <SloButton
            :disabled="loading"
            type="submit"
            class="fixed-button-width"
          >
            <SloLoader :loading="loading" size="md" color="white">
              {{ 'Update' | t }}
            </SloLoader>
          </SloButton>
        </div>
      </form>
    </article>
    <div class="mt-lg">
      <p class="fw-bold">{{ 'Currently shipping to US only.' | t }}</p>
    </div>
  </SloLoader>
</template>

<script>
import t from '../../main/i18n';
import {marketplaceApi} from '../apis/marketplace';
import SloLoader from '../../../lib/components/SloLoader.vue';
import SloField from '../../../lib/components/SloField.vue';
import SloLabel from '../../../lib/components/SloLabel.vue';
import SloInput from '../../../lib/components/SloInput.vue';
import SloButton from '../../../lib/components/SloButton.vue';
import AddressFormFields from '../components/AddressFormFields.vue';

export default {
  components: {
    SloLoader,
    SloField,
    SloLabel,
    SloInput,
    SloButton,
    AddressFormFields
  },
  filters: {
    t
  },
  data() {
    return {
      loading: false,
      loadingAddress: true,
      countries: [],
      regions: [],
      addressErrors: [],
      address: {
        firstName: '',
        attention: '',
        companyName: '',
        phone: '',
        country: {isocode: 'US'},
        line1: '',
        line2: '',
        town: '',
        region: {isocode: '', isocodeShort: ''},
        postalCode: ''
      }
    };
  },
  async created() {
    await marketplaceApi.getCountryCodes('SHIPPING')
      .then(async res => {
        this.countries = res.data?.countries;
        this.address.country.isocode = 'US';
        await this.getCountryRegions();
      })
      .catch(async() => {
        this.$snotify.error(t('Error loading countries'));
        await this.$router.push({name: 'marketplace-account-addresses'});
      });

    await marketplaceApi.getCurrentUserAddress(this.$route.params.id)
      .then(res => {
        this.address = res.data;
        this.loadingAddress = false;
      })
      .catch(async() => {
        this.$snotify.error(t('Address not found'));
        await this.$router.push({name: 'marketplace-account-addresses'});
      });
  },
  watch: {
    async 'address.country.isocode'(newValue, oldValue) {
      if (newValue !== oldValue) {
        await this.getCountryRegions();
      }
    }
  },
  methods: {
    async getCountryRegions() {
      this.address.region.isocode = '';
      this.address.region.isocodeShort = '';
      await marketplaceApi.getCountryRegions(this.address.country.isocode)
        .then(res => this.regions = res.data?.regions)
        .catch(() => this.regions = []);
    },
    async validateAddress() {
      if (this.address.country.isocode !== 'US') return true;

      this.loading = true;
      this.addressErrors = [];
      return await marketplaceApi.validateAddressWithStrikeIron(this.address)
        .then(res => {
          if (res.data) {
            // Update address data with validated address from StrikeIron
            this.address.line1 = res.data.line1;
            this.address.line2 = res.data.line2;
            this.address.town = res.data.town;
            this.address.region.isocode = res.data.region.isocode;
            this.address.region.isocodeShort = res.data.region.isocodeShort;
            this.address.postalCode = res.data.postalCode;
            return true;
          } else {
            this.loading = false;
            this.$snotify.clear();
            this.$snotify.error(t('Invalid address provided'));
            return false;
          }
        })
        .catch(e => {
          this.loading = false;
          if (e?.response?.data?.errors) {
            this.addressErrors = e?.response?.data?.errors;
          }
          this.$snotify.clear();
          this.$snotify.error(t('Invalid address provided'));
          return false;
        });
    },
    async handleSubmit() {
      if (!await this.validateAddress()) return;

      this.loading = true;
      await marketplaceApi.updateCurrentUserAddress(this.address)
        .then(async() => {
          this.$snotify.success(t('Address updated'));
          await this.$router.push({name: 'marketplace-account-addresses'});
        })
        .catch(() => {
          this.loading = false;
          this.$snotify.error(t('Error updating address'));
        });
    },
    handleAddressUpdate(address) {
      this.address = address;
    }
  }
};
</script>
